import React from "react";
import { useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { noise2D } from "@remotion/noise";
import { LightPillar } from "../../components/LightPillar";

// Fixed particle distribution for deterministic animation
const PARTICLES = Array.from({ length: 15 }, (_, i) => ({
  seedX: i * 0.43 + 2.5,
  seedY: i * 0.67 + 1.1,
  baseX: (i * 157.3) % 100, // percentage x
  baseY: (i * 113.7) % 100, // percentage y
  size: 3 + (i % 5),
  opacity: 0.08 + (i % 3) * 0.04,
  color: [
    "#FBD839", // yellow
    "#EE9EAC", // pink
    "#F45E2E", // orange
  ][i % 3],
}));

export const InkBackground: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Subtle sheen sweep across the dark canvas
  const sheenTranslate = interpolate(
    (frame % 250),
    [0, 250],
    [-width * 1.5, width * 1.5]
  );

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        background: "linear-gradient(135deg, #050608 0%, #090b0e 60%, #0d1014 100%)", // Dark Slate Black
        overflow: "hidden",
      }}
    >
      {/* ── WebGL Light Pillar Background ── */}
      <LightPillar
        topColor="#0196AF" // Teal
        bottomColor="#EE9EAC" // Pink
        intensity={0.4}
        rotationSpeed={0.2}
        glowAmount={0.003}
        pillarWidth={1.8}
        pillarHeight={0.4}
        noiseIntensity={0.12}
        pillarRotation={-10}
        interactive={false}
        mixBlendMode="normal"
        quality="high"
      />

      {/* ── Subtle Sheen Overlay ── */}
      <div
        style={{
          position: "absolute",
          top: "-50%",
          left: 0,
          width: "200%",
          height: "200%",
          background: "linear-gradient(115deg, transparent 40%, rgba(1, 150, 175, 0.02) 48%, rgba(1, 150, 175, 0.04) 50%, rgba(1, 150, 175, 0.02) 52%, transparent 60%)",
          transform: `translateX(${sheenTranslate}px) rotate(-15deg)`,
          pointerEvents: "none",
        }}
      />

      {/* ── SVG Grid and Accent Elements ── */}
      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          <pattern
            id="inkPorcelainGridDark"
            x="0"
            y="0"
            width="80"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            <line
              x1="0"
              y1="80"
              x2="80"
              y2="80"
              stroke="rgba(1, 150, 175, 0.05)" // dark grid line
              strokeWidth="1.2"
            />
            <line
              x1="80"
              y1="0"
              x2="80"
              y2="80"
              stroke="rgba(1, 150, 175, 0.05)" // dark grid line
              strokeWidth="1.2"
            />
          </pattern>
          
          {/* Radial ambient glow using teal */}
          <radialGradient id="radialAmbientGlowDark" cx="50%" cy="40%" r="70%">
            <stop offset="0%" stopColor="rgba(1, 150, 175, 0.05)" />
            <stop offset="80%" stopColor="rgba(5, 6, 8, 0)" />
          </radialGradient>
        </defs>

        {/* Ambient Center Glow */}
        <rect width={width} height={height} fill="url(#radialAmbientGlowDark)" />

        {/* Technical Grid Pattern */}
        <rect width={width} height={height} fill="url(#inkPorcelainGridDark)" />

        {/* Faint connecting lines between particles using noise */}
        {PARTICLES.slice(0, 7).map((p, i) => {
          const next = PARTICLES[(i + 1) % 7];
          const ax =
            (p.baseX / 100) * width +
            noise2D("cx" + i, p.seedX, frame * 0.0018) * 40;
          const ay =
            (p.baseY / 100) * height +
            noise2D("cy" + i, p.seedY, frame * 0.0018) * 40;
          const bx =
            (next.baseX / 100) * width +
            noise2D("cx" + (i + 1), next.seedX, frame * 0.0018) * 40;
          const by =
            (next.baseY / 100) * height +
            noise2D("cy" + (i + 1), next.seedY, frame * 0.0018) * 40;

          const dist = Math.hypot(bx - ax, by - ay);
          const opacity = dist < 320 ? (1 - dist / 320) * 0.05 : 0;

          return (
            <line
              key={`line-${i}`}
              x1={ax}
              y1={ay}
              x2={bx}
              y2={by}
              stroke={`rgba(1, 150, 175, ${opacity.toFixed(3)})`}
              strokeWidth="1"
            />
          );
        })}

        {/* Noise-driven floating colored accent particles */}
        {PARTICLES.map((p, i) => {
          const px =
            (p.baseX / 100) * width +
            noise2D("px" + i, p.seedX, frame * 0.0012) * 50;
          const py =
            (p.baseY / 100) * height +
            noise2D("py" + i, p.seedY, frame * 0.001) * 70;

          // Gentle breathing opacity
          const opacityBreathing =
            p.opacity +
            noise2D("po" + i, p.seedX + 3, frame * 0.003) * 0.03;

          const r = p.size;
          const isFilled = i % 2 === 0;

          return (
            <circle
              key={`part-${i}`}
              cx={px}
              cy={py}
              r={r}
              fill={isFilled ? p.color : "none"}
              stroke={!isFilled ? p.color : "none"}
              strokeWidth={!isFilled ? 1.2 : 0}
              style={{
                opacity: opacityBreathing,
              }}
            />
          );
        })}
      </svg>

      {/* Subtle Vignette & Top/Bottom Gradient shadow overlays for absolute legibility */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "linear-gradient(to bottom, rgba(5, 6, 8, 0.75) 0%, transparent 20%, transparent 80%, rgba(5, 6, 8, 0.75) 100%), radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(5, 6, 8, 0.6) 100%)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
